import React from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import ApiUtils from '../../../utils/ApiUtils';
import AccountsStore from '../../../stores/AccountsStore';
import AppStore from '../../../stores/AppStore';
import MoveMoneyStore from '../../../stores/MoveMoneyStore';
import MoveMoneySMEStore from '../../../stores/MoveMoneySMEStore';
import { PaymentAppSelector as Payments } from 'payments-lib';
import AccountsActionCreator from '../../../actions/AccountsActionCreator';
import InternetBankingActions from '../../../actions/InternetBankingActions';
import SiteHeaderActionCreator from '../../../actions/SiteHeaderActionCreator';
import {
  setSMEPaymentList,
  resetSMEBackUrl,
  resetPaymentAuthList,
} from '../../../actions/MoveMoneySMEActionCreator';
import { copCheckTriggeredHandler } from '../../../utils/COPUtils';
import paymentPropTypes from './paymentPropTypes';
import StringConstant from '../../../constants/StringConstants';
import UserManagementStore from '../../../stores/UserManagementStore';
import MoveMoneyActionCreator from '../../../actions/MoveMoneyActionCreator';
import FavouritesStore from '../../../stores/AccountsStore/FavouritesStore';
import PreferencesStore from '../../../stores/PreferencesStore';
import { BioCatchContext } from '../../BioCatchContext';
import { REQUEST_PAYMENT_CONTEXT } from '../../../config/BioCatch';
import envConfig from '../../../../static/config';

export const getAccountId = (
  { account_number: accountNumber, sort_code: sortCode, account_uuid },
  payees = []
) => {
  if (account_uuid) return account_uuid;
  const payee = payees.find(
    (payee) =>
      payee.to_account.account_number === accountNumber.toString() &&
      payee.to_account.sort_code === sortCode.toString()
  );
  if (payee) return payee.id;
  const account = AccountsStore.getAccountByAccountNumberSortCode(
    accountNumber,
    sortCode
  );
  if (account) return account.id;

  return null;
};

export const getAccountIdAndCheckName = (
  {
    account_number: accountNumber,
    sort_code: sortCode,
    account_uuid,
    display_name,
    name,
  },
  payees = []
) => {
  const nameToFind = display_name || name;
  if (account_uuid) return account_uuid;
  const possiblePayees = payees.filter(
    (payee) =>
      payee.to_account.account_number === accountNumber.toString() &&
      payee.to_account.sort_code === sortCode.toString()
  );

  if (possiblePayees.length === 1) return possiblePayees[0].id;
  if (possiblePayees.length > 1 && nameToFind) {
    const payee = possiblePayees.find(
      (p) =>
        _.get(p, 'to_account.name', '').toLowerCase() ===
        nameToFind.toLowerCase()
    );
    if (payee) return payee.id;
  }

  const account = AccountsStore.getAccountByAccountNumberSortCode(
    accountNumber,
    sortCode
  );
  if (account) return account.id;

  if (possiblePayees.length > 0) return possiblePayees[0].id;

  return null;
};

export const getPaymentsWithAccountId = (
  payments = [],
  paymentType,
  payees
) => {
  if (paymentType === 'BACS_CREDIT')
    return payments.map((payment) => ({
      ...payment,
      toAccount: getAccountId(payment.toAccount, payees),
    }));
  if (paymentType === 'BACS_DEBIT')
    return payments.map((payment) => ({
      ...payment,
      fromAccount: getAccountId(payment.fromAccount, payees),
    }));
  return payments.map((payment) => ({
    ...payment,
    fromAccount: getAccountId(payment.fromAccount),
    toAccount: getAccountIdAndCheckName(payment.toAccount, payees),
    rawPayment: payment,
  }));
};

class MoveMoneySME extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      isAuthorisationEdit: MoveMoneySMEStore.isAuthorisationEdit(),
      isAuthorisationEditById: MoveMoneySMEStore.isAuthorisationEditById(),
      isGroupApprovalsEnabled: PreferencesStore.getIsGroupApprovalsEnabled(),
      isManagePaymentEdit: MoveMoneySMEStore.isManagePaymentEdit(),
      paymentToEdit: MoveMoneySMEStore.getPaymentToEdit(),
      payments: MoveMoneySMEStore.getSMEPaymentList(),
      paymentDetails: MoveMoneySMEStore.getPaymentDetails(),
      backUrl: MoveMoneySMEStore.getBackUrl(),
      paymentType: MoveMoneySMEStore.getPaymentType(),
      templateInfo: MoveMoneySMEStore.getTemplateInfo(),
    };

    this.handleAddPayee = this.handleAddPayee.bind(this);
    this.onMoveMoneySMEStoreChange = this.onMoveMoneySMEStoreChange.bind(this);
    this.handlePaymentInReview = this.handlePaymentInReview.bind(this);
    this.resetPayments = this.resetPayments.bind(this);
  }

  componentWillMount() {
    MoveMoneyActionCreator.getFromPayeeList();
    MoveMoneySMEStore.addChangeListener(this.onMoveMoneySMEStoreChange);
  }

  componentWillUnmount() {
    if (
      this.state.isAuthorisationEdit ||
      this.state.isManagePaymentEdit ||
      this.state.isAuthorisationEditById
    ) {
      resetPaymentAuthList();
    }
    resetSMEBackUrl();
    MoveMoneySMEStore.removeChangeListener(this.onMoveMoneySMEStoreChange);
  }

  onMoveMoneySMEStoreChange() {
    this.setState({
      isAuthorisationEdit: MoveMoneySMEStore.isAuthorisationEdit(),
      isManagePaymentEdit: MoveMoneySMEStore.isManagePaymentEdit(),
      paymentToEdit: MoveMoneySMEStore.getPaymentToEdit(),
      isAuthorisationEditById: MoveMoneySMEStore.isAuthorisationEditById(),
      payments: MoveMoneySMEStore.getSMEPaymentList(),
      paymentDetails: MoveMoneySMEStore.getPaymentDetails(),
      backUrl: MoveMoneySMEStore.getBackUrl(),
      paymentType: MoveMoneySMEStore.getPaymentType(),
      templateInfo: MoveMoneySMEStore.getTemplateInfo(),
    });
  }
  getSessionData() {
    const { session } = this.props;
    return {
      clientContext: ApiUtils.getApiClientContext(),
      accessToken: session.accessToken,
      bankId: ApiUtils.getBankId(),
    };
  }
  handleRoute(route) {
    InternetBankingActions.navigateToWebTask(route);
  }

  handleBack(isEditByAuthorisation) {
    if (isEditByAuthorisation && this.state.backUrl !== 'WEB-MANAGE-PAYMENT') {
      InternetBankingActions.navigateToWebTask(
        StringConstant.WEB_TASK_PAYMENT_STATUS
      );
    } else {
      InternetBankingActions.navigateToWebTask(this.state.backUrl);
    }
  }

  handleComplete() {
    if (!FavouritesStore.isLargeCustomer()) {
      AccountsActionCreator.getAccountsList();
    }
  }
  handlePaymentInReview(inReview) {
    const { content } = this.props;

    const modalConfig = {
      displayLeavePopup: inReview,
      title: content.smeReviewWarningHeader,
      body: content.smeReviewWarningBody,
      secondaryButtonText: content.leaveThePage,
      primaryButtonText: content.continueWithPayment,
    };
    SiteHeaderActionCreator.setModalConfig(modalConfig);
  }

  handleAddPayee({ paymentType, event, paymentsWithDetails }) {
    const { accountToAddPayee } = paymentsWithDetails;
    const selectedAccount = AccountsStore.getAccountDetail(accountToAddPayee);
    MoveMoneyStore.setSelectedFromAccount(selectedAccount);
    setSMEPaymentList(paymentType, paymentsWithDetails);
    this.props.showAddPayeePage(event);
  }

  resetPayments() {
    const { showQuickLinks } = this.props;
    resetPaymentAuthList();
    InternetBankingActions.navigateToWebTask('WEB-OPEN-MOVEMONEY');
    showQuickLinks();
  }

  render() {
    const { payees, isBatchPayment, payment, hideQuickLinks } = this.props;
    const {
      isAuthorisationEdit,
      isAuthorisationEditById,
      isGroupApprovalsEnabled,
      isManagePaymentEdit,
      payments,
      paymentDetails = {},
      paymentType,
      templateInfo: { templateName } = {},
      paymentToEdit,
    } = this.state;
    const sessionCOPChecks = AppStore.getSessionCOPChecks();
    const paymentsWithAccounts =
      isManagePaymentEdit || isAuthorisationEdit
        ? getPaymentsWithAccountId(payments, paymentType, payees)
        : payments;
    const allAccounts = AccountsStore.getAll();
    const user = UserManagementStore.getCADetails();
    const isEditByAuthorisation =
      isAuthorisationEdit || isAuthorisationEditById;

    return (
      <BioCatchContext context={REQUEST_PAYMENT_CONTEXT}>
        <Payments
          accounts={allAccounts}
          copCheckTriggered={copCheckTriggeredHandler}
          payees={payees}
          session={this.getSessionData()}
          handleAddPayee={this.handleAddPayee}
          handleRoute={this.handleRoute}
          multiTransactions={isBatchPayment}
          hasGroupApprovalsEnabled={!!isGroupApprovalsEnabled}
          hasPaymentCapability={AccountsStore.SMEHasPaymentCapability()}
          payment={payment}
          envConfig={envConfig}
          payments={paymentsWithAccounts}
          isAuthorisationEdit={isAuthorisationEdit}
          resetPaymentList={this.resetPayments}
          handleBack={() => this.handleBack(isEditByAuthorisation)}
          handleComplete={this.handleComplete}
          handleDisplayFullwidth={hideQuickLinks}
          paymentDetails={paymentDetails}
          handlePaymentInReview={this.handlePaymentInReview}
          user={user}
          paymentType={paymentType}
          templateName={templateName}
          isManagePaymentEdit={isManagePaymentEdit}
          isAuthorisationEditById={isAuthorisationEditById}
          paymentToEdit={paymentToEdit}
          fetchParentBeneficiary={() =>
            MoveMoneyActionCreator.getFromPayeeList()
          }
          handleAccountsForNonLCVUser={() => AccountsStore.getAll()}
          sessionCOPChecks={sessionCOPChecks}
        />
      </BioCatchContext>
    );
  }
}

MoveMoneySME.propTypes = {
  payees: PropTypes.oneOf([
    PropTypes.shape([]),
    PropTypes.shape([
      {
        id: PropTypes.string,
        reference: PropTypes.string,
        account: PropTypes.shape({
          account_number: PropTypes.string.isRequired,
        }),
      },
    ]),
  ]).isRequired,
  isBatchPayment: PropTypes.bool,
  session: PropTypes.shape({
    accessToken: PropTypes.string.isRequired,
  }).isRequired,
  showAddPayeePage: PropTypes.func.isRequired,
  payment: paymentPropTypes,
  paymentAuthList: PropTypes.arrayOf(PropTypes.shape({})),
  hideQuickLinks: PropTypes.func,
  showQuickLinks: PropTypes.func,
  content: PropTypes.shape({
    smeReviewWarningHeader: PropTypes.string.isRequired,
    smeReviewWarningBody: PropTypes.string.isRequired,
    leaveThePage: PropTypes.string.isRequired,
    continueWithPayment: PropTypes.string.isRequired,
  }).isRequired,
};

MoveMoneySME.defaultProps = {
  payees: [],
  hideQuickLinks: () => {},
  showQuickLinks: () => {},
};
export default MoveMoneySME;
